import { ASSET_TIERS } from './asset-service.js';
/** The five §170.19 categories, in rendering order. */
export const SCOPE_CATEGORIES = [
    { key: 'cui', label: 'CUI Assets', treatment: 'assessed' },
    { key: 'security_protection', label: 'Security Protection Assets', treatment: 'assessed' },
    { key: 'contractor_risk_managed', label: 'Contractor Risk Managed', treatment: 'documented' },
    { key: 'specialized', label: 'Specialized Assets', treatment: 'documented' },
    { key: 'out_of_scope', label: 'Out-of-Scope', treatment: 'excluded' },
];
const TIER_ROLE = {
    identity: 'identity service',
    database: 'data store',
    web_front_end: 'web front end',
    middle_tier: 'middle tier',
    ai_service: 'AI service',
    endpoint_fleet: 'endpoint fleet',
    network: 'network boundary',
    other: 'component',
};
const assertTier = (t) => ASSET_TIERS.includes(t) ? t : 'other';
/** Category-level opening sentence; drives the paragraph's lead when assets exist. */
const CATEGORY_LEAD = {
    cui: (n) => `The assessment boundary includes ${n} CUI ${n === 1 ? 'asset' : 'assets'} that process, store, or transmit CUI and are assessed against all applicable Level 2 requirements.`,
    security_protection: (n) => `${n} Security Protection ${n === 1 ? 'asset' : 'assets'} ${n === 1 ? 'provides' : 'provide'} security capability to the CUI assets and ${n === 1 ? 'is' : 'are'} assessed against requirements relevant to that capability.`,
    contractor_risk_managed: (n) => `${n} Contractor Risk Managed ${n === 1 ? 'asset is' : 'assets are'} policy-isolated from CUI; access is documented and periodically reviewed.`,
    specialized: (n) => `${n} Specialized ${n === 1 ? 'asset is' : 'assets are'} documented with compensating controls (OT, IoT, test equipment, or Government-furnished equipment).`,
    out_of_scope: (n) => `${n} ${n === 1 ? 'asset is' : 'assets are'} separated from CUI and excluded from the assessment boundary.`,
};
const CATEGORY_EMPTY = {
    cui: 'No CUI assets are in scope for this period.',
    security_protection: 'No Security Protection assets are in scope for this period.',
    contractor_risk_managed: 'No Contractor Risk Managed assets have been recorded.',
    specialized: 'No Specialized assets have been recorded.',
    out_of_scope: 'No out-of-scope assets have been recorded.',
};
/** "AWS GovCloud VPC (network boundary)" with the optional rationale appended as its own sentence. */
function assetSentence(name, tier, rationale) {
    const role = TIER_ROLE[tier];
    const head = `${name.trim()} (${role})`;
    const rat = rationale?.trim();
    return rat ? `${head} — ${rat.endsWith('.') ? rat : `${rat}.`}` : `${head}.`;
}
/**
 * Render §2.1. Pass `drafts: {}` for production packages — any entry maps
 * an `asset_id` to an uncommitted {rationale, category} that overrides the
 * live membership and wraps the sentence in `{draft: true, text}`.
 */
export function renderSspSection21(scope, draftsByAssetId = {}) {
    // Draft overrides: an asset is in whatever category the draft says it is.
    const draftAssets = new Set(Object.keys(draftsByAssetId));
    const paragraphs = SCOPE_CATEGORIES.map((cat) => {
        const saved = scope.filter((s) => s.membership.category === cat.key && !draftAssets.has(s.asset.asset_id));
        const drafted = scope.filter((s) => draftAssets.has(s.asset.asset_id) && draftsByAssetId[s.asset.asset_id].category === cat.key);
        const total = saved.length + drafted.length;
        const lines = [];
        if (!total) {
            lines.push(CATEGORY_EMPTY[cat.key]);
        }
        else {
            lines.push(CATEGORY_LEAD[cat.key](total));
            for (const s of saved) {
                lines.push(assetSentence(s.asset.name, assertTier(s.asset.tier), s.membership.rationale));
            }
            for (const s of drafted) {
                const draft = draftsByAssetId[s.asset.asset_id];
                lines.push({ draft: true, text: assetSentence(s.asset.name, assertTier(s.asset.tier), draft.rationale) });
            }
        }
        return {
            category_key: cat.key,
            category_label: cat.label,
            treatment: cat.treatment,
            asset_count: total,
            lines,
        };
    });
    return { paragraphs };
}
//# sourceMappingURL=scope-ssp-service.js.map